import { useEffect, useState } from "react";
import usePostcodeGroupSingle from "../../data/usePostcodeGroupSingle";
import PostcodeAreasTable from "./PostcodeAreasTable";
import { CreatePostcodePriceRequest, IPostcodeGroup, IPostcodePrice } from './../../models/PostcodeGroup';
import usePostcodePriceMutations from "../../data/usePostcodePriceMutations";
import { TextInput } from "../shared/Inputs/TextInput";
import { useForm } from "react-hook-form";
import useDefaultCRUDHandlers from "../../hooks/useDefaultCRUDHandlers";
import { NumberInput } from "../shared/Inputs/NumberInput";
import { ButtonInput } from "../shared/Inputs/ButtonInput";
import AlphaNumericSort from "../../utils/alphaNumericSort";
import SwitchInput from "../shared/Inputs/SwitchInput";
import useNotification from "../notifications/useNotifications";
import { validateFormFields, ValidationField } from "../../utils/formValidation";

interface FormValues {
  postcode: string;
  price: number;
  onlyGoodEngineCondition: boolean;
  onlyGoodBodyCondition: boolean;
  isActive: boolean;
  thresholdPrice: number;
}

export default function EditPostcodeAreas({ postcodeGroupId }: { postcodeGroupId: string }) {
  const { postcodeGroup } = usePostcodeGroupSingle(postcodeGroupId);
  const { create } = usePostcodePriceMutations();
  const { saveHandlers } = useDefaultCRUDHandlers("Postcode Area");
  const { register, handleSubmit, reset, watch, setValue } = useForm<FormValues>( {
    defaultValues: {
      postcode: "",
      price: 0,
      thresholdPrice: 0,
      onlyGoodEngineCondition: false,
      onlyGoodBodyCondition: false,
      isActive: true
    }
  });
  const [basePrice, setBasePrice] = useState<number>(0);
  const [postcodePrices, setPostcodePrices] = useState<IPostcodePrice[]>([]);
  const { addNotification } = useNotification();

  const validationFields: ValidationField[] = [
    { field: 'postcode', label: 'Postcode Sub-Area' },
    { field: 'price', label: 'Price per Tonne', minValue: 0 },
    { field: 'thresholdPrice', label: 'Minimum Accepted Price', minValue: 0 }
  ];

  const onSubmit = (data: FormValues) => {
    if (!validateFormFields(data, validationFields, addNotification)) {
      return;
    }

    const newPostcodePrice: CreatePostcodePriceRequest = {
      ...data,
      postcodeGroupId: postcodeGroupId
    }

    create.mutate(newPostcodePrice, {
      onSuccess: () => {
        reset();
        saveHandlers.onSuccess();
      },
      onError: saveHandlers.onError
    });
  };

  useEffect(() => {
    if (!postcodeGroup.isLoading && postcodeGroup.data) {
      setPostcodePrices(postcodeGroup.data.data.postcodePrices);
      setBasePrice(postcodeGroup.data.data.basePrice);

      reset({
        price: postcodeGroup.data.data.basePrice,
        onlyGoodEngineCondition: postcodeGroup.data.data.onlyGoodEngineCondition,
        onlyGoodBodyCondition: postcodeGroup.data.data.onlyGoodBodyCondition,
        thresholdPrice: postcodeGroup.data.data.thresholdPrice,
        isActive: postcodeGroup.data.data.isActive,
      });
    }
  }, [postcodeGroup.data, postcodeGroup.isLoading]);

  if (postcodeGroup.isLoading) return <></>;


  return (
    <div className="flex flex-col gap-y-4 px-4 sm:px-0">
      <h1 className="text-xl font-semibold text-gray-900">{"Edit Postcode Area Prices"}</h1>
      <small className="text-gray-500"><i>
        Override postcode group default price with area specific pricing
      </i></small>

      <form onSubmit={handleSubmit(onSubmit)} className="border-t border-gray-200 pt-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-4">
          <TextInput label={"Postcode Sub-Area (e.g. 'SW1', 'PL4')"} register={register} registerName={"postcode"} />
          <NumberInput label={"Price per Tonne (£)"} register={register} registerName={"price"} minVal={null} />
          <div className="sm:col-span-2">
            <NumberInput label={"Minimum Accepted Price"} register={register} registerName={"thresholdPrice"} minVal={0} />
          </div>
          <div className="sm:col-span-2">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <SwitchInput label="Only Good Engine" registerName="onlyGoodEngineCondition" register={register} watch={watch} setValue={setValue} />
              <SwitchInput label="Only Good Body" registerName="onlyGoodBodyCondition" register={register} watch={watch} setValue={setValue} />
              <SwitchInput label="Active" registerName="isActive" register={register} watch={watch} setValue={setValue} />
            </div>
          </div>
        </div>
        <div className="mt-6">
          <ButtonInput label={"Add"} isSubmit={true} onClick={() => { }} classes={"w-full sm:w-auto"} />
        </div>
      </form>

      <div className="border-t border-gray-200 pt-4">
        <div className="overflow-x-auto">
          <PostcodeAreasTable postcodePrices={postcodePrices} outcode={postcodeGroup.data?.data.postcode!} />
        </div>
      </div>
    </div>
  )


}